export type RouteId =
  | "dashboard" | "program-registry" | "program-workspace" | "program-roadmap"
  | "product-registry" | "product-workspace"
  | "capability-registry" | "capability-workspace"
  | "work-package-registry" | "work-package-workspace"
  | "execution-plan-registry" | "execution-plan-workspace"
  | "execution-center" | "execution-center-run" | "flow-trace"
  | "autonomous-orchestration" | "action-center" | "policy-administration" | "delivery-intelligence"
  | "workers" | "ai-providers" | "ai-models" | "ai-workers" | "approved-tools"
  | "approvals" | "evidence" | "audit" | "operations"
  | "administration-users" | "administration-backup-recovery" | "administration-settings" | "cockpit-boundary" | "not-found";

export interface RouteDefinition {
  id: RouteId;
  pattern: string;
  label: string;
  icon: string;
  navigation?: boolean;
  group?: NavigationGroup;
}

export type NavigationGroup = "Overview" | "Portfolio" | "Delivery" | "AI Workforce"
  | "AI Administration" | "Governance" | "Operations" | "Administration";

export const navigationGroups: NavigationGroup[] = [
  "Overview", "Portfolio", "Delivery", "AI Workforce", "AI Administration",
  "Governance", "Operations", "Administration"
];

export interface RouteMatch {
  route: RouteDefinition;
  params: Record<string, string>;
  redirectTo?: string;
}

export const routes: RouteDefinition[] = [
  {id: "dashboard", pattern: "/dashboard", label: "Dashboard", icon: "◫", navigation: true, group: "Overview"},
  {id: "program-registry", pattern: "/programs", label: "Programs", icon: "◇", navigation: true, group: "Portfolio"},
  {id: "program-roadmap", pattern: "/programs/:id/roadmap", label: "Program roadmap", icon: "◇"},
  {id: "program-workspace", pattern: "/programs/:id", label: "Program", icon: "◇"},
  {id: "product-registry", pattern: "/products", label: "Products", icon: "□", navigation: true, group: "Portfolio"},
  {id: "product-workspace", pattern: "/products/:id", label: "Product", icon: "□"},
  {id: "capability-registry", pattern: "/capabilities", label: "Capabilities", icon: "◈", navigation: true, group: "Portfolio"},
  {id: "capability-workspace", pattern: "/capabilities/:id", label: "Capability", icon: "◈"},
  {id: "work-package-registry", pattern: "/work-packages", label: "Work packages", icon: "▤", navigation: true, group: "Delivery"},
  {id: "work-package-workspace", pattern: "/work-packages/:id", label: "Work package", icon: "▤"},
  {id: "execution-plan-registry", pattern: "/execution-plans", label: "Execution plans", icon: "⌁", navigation: true, group: "Delivery"},
  {id: "execution-plan-workspace", pattern: "/execution-plans/:id", label: "Execution plan", icon: "⌁"},
  {id: "autonomous-orchestration", pattern: "/execution-center/orchestration", label: "Autonomous orchestration", icon: "↻", navigation: true, group: "Operations"},
  {id: "execution-center", pattern: "/execution-center", label: "Execution Center", icon: "▶", navigation: true, group: "Operations"},
  {id: "execution-center-run", pattern: "/execution-center/:id", label: "Execution", icon: "▶"},
  {id: "flow-trace", pattern: "/flow-traces/:id", label: "Flow trace", icon: "⇢"},
  {id: "workers", pattern: "/workers", label: "Worker registry", icon: "◎", navigation: true, group: "AI Workforce"},
  {id: "ai-providers", pattern: "/ai-administration/providers", label: "Providers", icon: "◌", navigation: true, group: "AI Administration"},
  {id: "ai-models", pattern: "/ai-administration/models", label: "Models", icon: "◍", navigation: true, group: "AI Administration"},
  {id: "ai-workers", pattern: "/ai-administration/workers", label: "Workers", icon: "◎", navigation: true, group: "AI Administration"},
  {id: "approved-tools", pattern: "/ai-administration/approved-tools", label: "Approved tools", icon: "⌘", navigation: true, group: "AI Administration"},
  {id: "approvals", pattern: "/approvals", label: "Approvals", icon: "✓", navigation: true, group: "Governance"},
  {id: "action-center", pattern: "/action-center", label: "Action Center", icon: "!", navigation: true, group: "Governance"},
  {id: "evidence", pattern: "/evidence", label: "Evidence", icon: "▱", navigation: true, group: "Governance"},
  {id: "audit", pattern: "/audit", label: "Audit", icon: "≡", navigation: true, group: "Governance"},
  {id: "operations", pattern: "/operations", label: "Health & operations", icon: "◉", navigation: true, group: "Operations"},
  {id: "administration-users", pattern: "/administration/users", label: "Users & Roles", icon: "♙", navigation: true, group: "Administration"},
  {id: "policy-administration", pattern: "/administration/orchestration", label: "Orchestration policy", icon: "⚖", navigation: true, group: "Administration"},
  {id: "delivery-intelligence", pattern: "/delivery-intelligence", label: "Delivery intelligence", icon: "△", navigation: true, group: "Delivery"},
  {id: "administration-backup-recovery", pattern: "/administration/backup-recovery", label: "Backup & Recovery", icon: "↻", navigation: true, group: "Administration"},
  {id: "administration-settings", pattern: "/administration/settings", label: "Settings", icon: "⚙", navigation: true, group: "Administration"},
  {id: "cockpit-boundary", pattern: "/administration/integrations/cockpit", label: "Cockpit boundary", icon: "↔"},
  {id: "not-found", pattern: "/404", label: "Not found", icon: "?"}
];

const aliases: Record<string, string> = {
  "/": "/dashboard",
  "/portfolio": "/programs?view=portfolio",
  "/board": "/execution-plans?view=board",
  "/workspace": "/execution-plans?view=workspaces",
  "/metrics": "/operations?view=metrics",
  "/gitlab": "/operations?view=gitlab",
  "/workspaces": "/execution-plans?view=workspaces",
  "/orchestration": "/execution-plans?view=orchestration",
  "/factory-floor": "/execution-plans?view=factory-floor",
  "/settings": "/administration/settings",
  "/cockpit-compatibility": "/administration/integrations/cockpit"
};

export function resolveRoute(location: string): RouteMatch {
  const url = new URL(location, "https://softwarefactory.invalid");
  const aliasTarget = aliases[url.pathname];
  const redirectTo = aliasTarget ? canonicalAliasRedirect(url, aliasTarget) : undefined;
  const effective = redirectTo
    ? new URL(redirectTo, "https://softwarefactory.invalid") : url;
  for (const route of routes) {
    if (route.id === "not-found") continue;
    const params = match(route.pattern, effective.pathname);
    if (params) return {route, params, redirectTo};
  }
  return {route: routes.find(route => route.id === "not-found")!, params: {}};
}

function canonicalAliasRedirect(incoming: URL, aliasTarget: string): string {
  const canonical = new URL(aliasTarget, "https://softwarefactory.invalid");
  const search = new URLSearchParams(incoming.search);
  const canonicalKeys = new Set(canonical.searchParams.keys());
  for (const key of canonicalKeys) search.delete(key);
  for (const [key, value] of canonical.searchParams) search.append(key, value);
  const query = search.toString();
  return `${canonical.pathname}${query ? `?${query}` : ""}${canonical.hash || incoming.hash}`;
}

export function isRouteActive(location: string, route: RouteDefinition): boolean {
  const matchResult = resolveRoute(location);
  if (route.id === "program-registry") return matchResult.route.id.startsWith("program-");
  if (route.id === "product-registry") return matchResult.route.id.startsWith("product-");
  if (route.id === "capability-registry") return matchResult.route.id.startsWith("capability-");
  if (route.id === "work-package-registry") return matchResult.route.id.startsWith("work-package-");
  if (route.id === "execution-plan-registry") return matchResult.route.id.startsWith("execution-plan-");
  if (route.id === "execution-center") return matchResult.route.id.startsWith("execution-center");
  return matchResult.route.id === route.id;
}

function match(pattern: string, pathname: string): Record<string, string> | null {
  const expected = parts(pattern);
  const actual = parts(pathname);
  if (expected.length !== actual.length) return null;
  const params: Record<string, string> = {};
  for (let index = 0; index < expected.length; index++) {
    const segment = expected[index];
    if (segment.startsWith(":")) {
      try {
        params[segment.slice(1)] = decodeURIComponent(actual[index]);
      } catch {
        return null;
      }
    } else if (segment !== actual[index]) return null;
  }
  return params;
}

function parts(path: string): string[] {
  return path.split("/").filter(Boolean);
}
